// Copyright (C) 2025, Lifefinder Systems International AB, all rights reserved.
import React, { useState, useEffect } from 'react';

import type {
  ReplaceDeviceModalProps,
  ReserveDevice,
  ReplaceResult,
  DeviceCategory,
} from '../../../types';
import { devicesAPI } from '../../../utils/apiClient';
import { Button } from '../../common/Button';

/**
 * ReplaceDeviceModal Component
 *
 * Modal for replacing a device with a reserve unit
 * - Fetches available reserve devices of the same category
 * - Shows what data will be copied
 * - Performs the replacement via API
 */
export const ReplaceDeviceModal: React.FC<ReplaceDeviceModalProps> = ({
  device,
  deviceCategory,
  onReplace,
  onCancel,
}) => {
  const [reserves, setReserves] = useState<ReserveDevice[]>([]);
  const [selectedReserveId, setSelectedReserveId] = useState<string>('');
  const [replacementReason, setReplacementReason] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [replacing, setReplacing] = useState<boolean>(false);

  // Fetch reserve devices on mount
  useEffect(() => {
    fetchReserves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceCategory]);

  const fetchReserves = async (): Promise<void> => {
    try {
      setLoading(true);

      const data = await devicesAPI.listReserves(deviceCategory);
      if (data.success) {
        setReserves(data.reserves || []);
      } else {
        setError(data.error || 'Failed to fetch reserve devices');
      }
    } catch (err) {
      console.error('Error fetching reserves:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Get device ID from various device types
  const getDeviceId = (): string => {
    if ('device_id' in device && typeof device.device_id === 'string') {
      return device.device_id;
    }
    if ('deveui' in device && typeof device.deveui === 'string') {
      return device.deveui;
    }
    if ('id' in device && typeof device.id === 'string') {
      return device.id;
    }
    return '';
  };

  const handleReplace = async (): Promise<void> => {
    if (!selectedReserveId) {
      alert('Please select a reserve device');
      return;
    }

    const deviceId = getDeviceId();

    if (
      !window.confirm(
        `Are you sure you want to replace ${deviceId} with ${selectedReserveId}?\n\n` +
          `This will:\n` +
          `- Copy description and checkpoint settings\n` +
          `- Copy all category-specific metadata\n` +
          `- Mark the old device as retired\n` +
          `- Activate the new device\n\n` +
          `This action cannot be undone!`
      )
    ) {
      return;
    }

    try {
      setReplacing(true);

      const data: ReplaceResult & { error?: string } = await devicesAPI.replace(
        deviceId,
        selectedReserveId,
        replacementReason || null
      );
      if (data.success) {
        alert(
          `Successfully replaced device!\n\nOld: ${data.old_device_id}\nNew: ${data.new_device_id}`
        );
        if (onReplace) onReplace(data);
      } else {
        throw new Error(data.error || 'Replacement failed');
      }
    } catch (err) {
      console.error('Error replacing device:', err);
      alert(`Failed to replace device: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setReplacing(false);
    }
  };

  const getCategoryDisplayName = (category: DeviceCategory): string => {
    const names: Record<DeviceCategory, string> = {
      lf_beacon: 'Beacon',
      lf_checkin: 'Check-in Station',
      lf_gateway: 'Gateway',
      lf_device: 'Device',
    };
    return names[category] || category;
  };

  // Get device properties safely
  const deviceId = getDeviceId();
  const deviceName = 'name' in device ? device.name : undefined;
  const deviceDescription = 'description' in device ? device.description : undefined;
  const deviceDeck = 'deck' in device ? device.deck : undefined;
  const deviceSide = 'side' in device ? device.side : undefined;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        // flex-start + paddingTop instead of center: prevents modal from extending below the visible
        // viewport on iOS where 100vh includes browser chrome behind the address/tab bar
        alignItems: 'flex-start',
        paddingTop: 'max(20px, 5vh)',
        zIndex: 4000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '1vmin',
          maxWidth: '600px',
          width: '90%',
          // dvh (dynamic viewport height) excludes browser chrome — ensures modal fits visible area on iOS
          maxHeight: 'min(90vh, 85dvh)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            overflowY: 'scroll',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            flex: 1,
            padding: '2.5vmin',
            overscrollBehavior: 'contain',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2vmin',
              borderBottom: '2px solid #ddd',
              paddingBottom: '1.5vmin',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '2.5vmin', color: '#333' }}>
              🔄 Replace {getCategoryDisplayName(deviceCategory)}
            </h2>
            <Button
              text="✕"
              onClick={onCancel}
              style={{
                background: '#f44',
                color: 'white',
                fontSize: '2vmin',
                padding: '0.5vmin 1vmin',
                border: 'none',
                borderRadius: '0.3vmin',
                cursor: 'pointer',
              }}
            />
          </div>

          {/* Current device info */}
          <div
            style={{
              background: '#f9f9f9',
              padding: '1.5vmin',
              borderRadius: '0.5vmin',
              marginBottom: '2vmin',
              border: '1px solid #ddd',
            }}
          >
            <h3 style={{ margin: '0 0 1vmin 0', fontSize: '1.8vmin', color: '#666' }}>
              Current Device:
            </h3>
            <div style={{ fontSize: '1.6vmin', lineHeight: '2' }}>
              <div>
                <strong>Name:</strong> {deviceName || 'N/A'}
              </div>
              <div>
                <strong>Device ID:</strong> {deviceId}
              </div>
              <div>
                <strong>Description:</strong> {deviceDescription || 'N/A'}
              </div>
              {deviceDeck !== undefined && (
                <div>
                  <strong>Deck:</strong> {deviceDeck}
                </div>
              )}
              {deviceSide && (
                <div>
                  <strong>Side:</strong> {deviceSide}
                </div>
              )}
            </div>
          </div>

          {/* Reserve device selection */}
          <div style={{ marginBottom: '2vmin' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '0.8vmin',
                fontWeight: 'bold',
                fontSize: '1.8vmin',
                color: '#333',
              }}
            >
              Select Reserve Device:
            </label>

            {loading ? (
              <div style={{ padding: '2vmin', textAlign: 'center', color: '#666' }}>
                Loading reserve devices...
              </div>
            ) : error ? (
              <div style={{ padding: '2vmin', textAlign: 'center', color: '#f44' }}>
                Error: {error}
              </div>
            ) : reserves.length === 0 ? (
              <div
                style={{
                  padding: '2vmin',
                  textAlign: 'center',
                  color: '#ff9800',
                  background: '#fff3e0',
                  borderRadius: '0.5vmin',
                  border: '1px solid #ff9800',
                }}
              >
                ⚠️ No reserve {getCategoryDisplayName(deviceCategory).toLowerCase()}s available
              </div>
            ) : (
              <select
                value={selectedReserveId}
                onChange={(e) => setSelectedReserveId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '1.2vmin',
                  fontSize: '1.6vmin',
                  border: '1px solid #ccc',
                  borderRadius: '0.5vmin',
                  boxSizing: 'border-box',
                  background: 'white',
                }}
              >
                <option value="">-- Select a reserve device --</option>
                {reserves.map((reserve) => (
                  <option key={reserve.deveui} value={reserve.deveui}>
                    {reserve.name} ({reserve.deveui}) - {reserve.description || 'No description'}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Replacement reason */}
          <div style={{ marginBottom: '2vmin' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '0.8vmin',
                fontWeight: 'bold',
                fontSize: '1.8vmin',
                color: '#333',
              }}
            >
              Reason for Replacement (optional):
            </label>
            <textarea
              value={replacementReason}
              onChange={(e) => setReplacementReason(e.target.value)}
              placeholder="e.g. Hardware failure, battery depleted, routine maintenance..."
              rows={3}
              style={{
                width: '100%',
                padding: '1vmin',
                fontSize: '1.6vmin',
                border: '1px solid #ccc',
                borderRadius: '0.5vmin',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>

          {/* What will be copied */}
          <div
            style={{
              background: '#e3f2fd',
              padding: '1.5vmin',
              borderRadius: '0.5vmin',
              marginBottom: '2vmin',
              border: '1px solid #2196F3',
            }}
          >
            <h3 style={{ margin: '0 0 1vmin 0', fontSize: '1.8vmin', color: '#1976D2' }}>
              ℹ️ What will be copied:
            </h3>
            <ul
              style={{
                margin: '0.5vmin 0 0 2vmin',
                padding: 0,
                fontSize: '1.6vmin',
                lineHeight: '2',
              }}
            >
              <li>Description and checkpoint settings</li>
              {deviceCategory === 'lf_beacon' && (
                <>
                  <li>Deck, side, channel</li>
                  <li>MAC address and frame number</li>
                  <li>Expected state (on/off/auto)</li>
                </>
              )}
              {deviceCategory === 'lf_checkin' && (
                <>
                  <li>Location and zone</li>
                  <li>Active status</li>
                </>
              )}
            </ul>
            <div
              style={{
                marginTop: '1vmin',
                fontSize: '1.4vmin',
                color: '#666',
                fontStyle: 'italic',
              }}
            >
              Note: The new device will keep its own name/ID
            </div>
          </div>
        </div>
        {/* end scrollable content area */}

        {/* Footer — always visible, outside the scrollable area */}
        <div
          style={{
            flexShrink: 0,
            padding: '1.5vmin 2.5vmin',
            borderTop: '1px solid #eee',
            background: 'white',
          }}
        >
          <div style={{ display: 'flex', gap: '1vmin' }}>
            <Button
              text={replacing ? 'Replacing...' : '🔄 Confirm Replace'}
              onClick={handleReplace}
              disabled={!selectedReserveId || replacing || reserves.length === 0}
              style={{
                flex: 1,
                background:
                  !selectedReserveId || replacing || reserves.length === 0 ? '#ccc' : '#FF9800',
                color: 'white',
                fontSize: '1.8vmin',
                padding: '1.2vmin 1.5vmin',
                border: 'none',
                borderRadius: '0.5vmin',
                cursor:
                  !selectedReserveId || replacing || reserves.length === 0
                    ? 'not-allowed'
                    : 'pointer',
                opacity: !selectedReserveId || replacing || reserves.length === 0 ? 0.6 : 1,
                fontWeight: 'bold',
              }}
            />
            <Button
              text="Cancel"
              onClick={onCancel}
              disabled={replacing}
              style={{
                flex: 1,
                background: '#ccc',
                color: '#333',
                fontSize: '1.8vmin',
                padding: '1.2vmin 1.5vmin',
                border: 'none',
                borderRadius: '0.5vmin',
                cursor: replacing ? 'not-allowed' : 'pointer',
                opacity: replacing ? 0.6 : 1,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplaceDeviceModal;
